import type {BigBagResumen, OrdenInventario} from '@cnc/contracts';
import {Link, useLocation} from 'react-router';
import {tr} from '../i18n/idioma';
import {CAMPO_BIG_BAG} from './campos-big-bag';
import {CeldaDeBigBag} from './celda-big-bag';
import {DEFINICION_INVENTARIO} from './columnas-inventario';
import {Tabla} from './tabla';

type Props = {
  filas: BigBagResumen[];
  columnasVisibles: string[];
  orden: OrdenInventario;
  direccion: 'asc' | 'desc';
  onOrdenar: (orden: OrdenInventario) => void;
  onReordenar: (ids: string[]) => void;
  cargando?: boolean;
  numeracionDesde?: number;
  // Casillas de seleccion (cambiar exportacion, cambio en lote, eliminar).
  seleccion?: {
    seleccionada: (fila: BigBagResumen) => boolean;
    onAlternar: (filas: BigBagResumen[], seleccionar: boolean) => void;
  };
  // Edicion rapida: las celdas de los datos editables se cambian ahi mismo.
  // `puedeEditar` deja por fuera los exportados para quien no es administrador.
  edicion?: {puedeEditar: (fila: BigBagResumen) => boolean};
};

// Tabla del inventario: la tabla generica con las columnas de los Big Bags,
// el numero como enlace al detalle, el estado como chip y la exportacion como
// enlace al seguimiento.
export function TablaInventario({edicion, ...props}: Props) {
  // La ficha del Big Bag vuelve a esta misma direccion (con los filtros puestos).
  const {pathname, search} = useLocation();
  const volver = {volver: `${pathname}${search}`};
  return (
    <Tabla
      {...props}
      definicion={DEFINICION_INVENTARIO}
      claveDeFila={fila => fila.id}
      mensajeVacio={tr('No hay Big Bags que cumplan estos filtros.', 'No Big Bags match these filters.')}
      celda={(columna, fila) => {
        const campo = edicion && edicion.puedeEditar(fila) ? CAMPO_BIG_BAG.get(columna.id) : undefined;
        if (campo) return <CeldaDeBigBag fila={fila} campo={campo} texto={columna.valor(fila)} />;
        if (columna.id === 'bbNo')
          return (
            <Link
              to={`/inventario/${encodeURIComponent(fila.bbNo)}`}
              state={volver}
              className="font-medium text-verde hover:underline"
            >
              {fila.bbNo}
            </Link>
          );
        if (columna.id === 'estado') return <Estado estado={fila.estado} texto={columna.valor(fila)} />;
        if (columna.id === 'exportacion' && fila.exportacion)
          return (
            <Link to={`/seguimiento/${encodeURIComponent(fila.exportacion)}`} className="text-verde hover:underline">
              {fila.exportacion}
            </Link>
          );
        return undefined;
      }}
    />
  );
}

const COLOR_DE_ESTADO: Record<BigBagResumen['estado'], string> = {
  available: 'bg-verde-claro text-verde-oscuro',
  assigned: 'bg-dorado-claro text-tinta',
  exported: 'bg-papel text-apagado',
};

export function Estado({estado, texto}: {estado: BigBagResumen['estado']; texto: string}) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COLOR_DE_ESTADO[estado]}`}>{texto}</span>;
}
